
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import { TrendingUp, Clock, Users, BarChart3 } from "lucide-react";
import { Event } from "@/types/event";
import { supabase } from "@/integrations/supabase/client";
import { analyticsService } from "@/services/analyticsService";

interface AnalyticsData {
  day: string;
  votes: number;
  participants: number;
}

interface VotingPattern {
  hour: number;
  votes: number;
}

interface AdvancedAnalyticsProps {
  event: Event;
}

export const AdvancedAnalytics = ({ event }: AdvancedAnalyticsProps) => {
  const [timeSeriesData, setTimeSeriesData] = useState<AnalyticsData[]>([]);
  const [votingPatterns, setVotingPatterns] = useState<VotingPattern[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<Array<{ name: string; value: number }>>([]);
  const [geoData, setGeoData] = useState<Array<{ location: string; votes: number }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAdvancedAnalytics();
  }, [event.id]);

  const loadAdvancedAnalytics = async () => {
    try {
      // Load voting history over time
      const { data: votes, error: votesError } = await supabase
        .from('event_votes')
        .select('created_at')
        .eq('event_id', event.id)
        .order('created_at');

      if (votesError) throw votesError;

      // Process time series data
      const votesGrouped = votes?.reduce((acc: Record<string, number>, vote) => {
        const day = new Date(vote.created_at).toISOString().split('T')[0];
        acc[day] = (acc[day] || 0) + 1;
        return acc;
      }, {}) || {};

      const timeSeriesData = Object.entries(votesGrouped)
        .map(([day, votes]) => ({
          day: new Date(day).toLocaleDateString(),
          votes: votes as number,
          participants: event.guests.filter(g => g.hasVoted).length,
        }))
        .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());

      setTimeSeriesData(timeSeriesData);

      // Process voting patterns by hour
      const hourlyVotes = votes?.reduce((acc: Record<number, number>, vote) => {
        const hour = new Date(vote.created_at).getHours();
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
      }, {}) || {};

      const votingPatterns = Array.from({ length: 24 }, (_, hour) => ({
        hour,
        votes: hourlyVotes[hour] || 0,
      }));

      setVotingPatterns(votingPatterns);

      // Load device type analytics
      const deviceAnalytics = await analyticsService.getDeviceTypeDistribution(event.id);
      setDeviceTypes(deviceAnalytics);

      // For geographic data, we'd need to implement IP geolocation
      // For now, we'll leave this empty as it requires additional setup
      setGeoData([]);
    } catch (error) {
      console.error('Error loading advanced analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEngagementRate = () => {
    if (event.guests.length === 0) return 0;
    return Math.round((event.totalVoters / event.guests.length) * 100);
  };

  const getAverageVotesPerOption = () => {
    if (event.options.length === 0) return 0;
    const totalVotes = event.options.reduce((sum, option) => sum + option.votes, 0);
    return Math.round(totalVotes / event.options.length * 10) / 10;
  };

  const getMostActiveHour = () => {
    const maxVotes = Math.max(...votingPatterns.map(p => p.votes));
    const mostActiveHour = votingPatterns.find(p => p.votes === maxVotes);
    return mostActiveHour ? `${mostActiveHour.hour}:00` : 'N/A';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading advanced analytics...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Engagement Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getEngagementRate()}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Avg Votes/Option
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getAverageVotesPerOption()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Most Active Hour
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getMostActiveHour()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4" />
              Response Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {event.guests.length > 0 ? Math.round((event.totalVoters / event.guests.length) * 100) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="timeline" className="space-y-4">
        <TabsList>
          <TabsTrigger value="timeline">Voting Timeline</TabsTrigger>
          <TabsTrigger value="patterns">Voting Patterns</TabsTrigger>
          <TabsTrigger value="devices">Device Types</TabsTrigger>
          <TabsTrigger value="geography">Geography</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>Voting Activity Over Time</CardTitle>
              <CardDescription>Track how voting activity progressed throughout the event</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="votes" 
                    stroke="#8884d8" 
                    fill="#8884d8" 
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patterns">
          <Card>
            <CardHeader>
              <CardTitle>Voting Patterns by Hour</CardTitle>
              <CardDescription>See when participants are most active throughout the day</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={votingPatterns}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="hour" 
                    tickFormatter={(hour) => `${hour}:00`}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(hour) => `${hour}:00`}
                    formatter={(value) => [value, 'Votes']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="votes" 
                    stroke="#82ca9d" 
                    strokeWidth={2}
                    dot={{ fill: '#82ca9d' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices">
          <Card>
            <CardHeader>
              <CardTitle>Device Types</CardTitle>
              <CardDescription>Distribution of votes by device type</CardDescription>
            </CardHeader>
            <CardContent>
              {deviceTypes.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={deviceTypes}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {deviceTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#0088FE', '#00C49F', '#FFBB28'][index % 3]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  <div className="text-center">
                    <p>No device data yet</p>
                    <p className="text-sm">Data will appear as users interact with your event</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="geography">
          <Card>
            <CardHeader>
              <CardTitle>Geographic Distribution</CardTitle>
              <CardDescription>Votes by location</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                <div className="text-center">
                  <p>Geographic tracking not currently implemented</p>
                  <p className="text-sm">This feature requires IP geolocation or user location data</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
